import React, { useState } from "react";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Lock,
  Star,
  ChevronDown,
} from "lucide-react";
import {
  companyInfo,
  serviceTypes,
  materialClasses,
  reviews,
} from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  city: "",
  serviceType: serviceTypes[0],
  materialClass: materialClasses[0],
  yards: "",
  description: "",
};

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({
        title: "Missing details",
        description: "Please add your name and phone number.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setForm(initialForm);
      toast({
        title: "Quote request received!",
        description: "We usually reply with dispatch slots within 2 hours.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us directly.";
      toast({ title: "Submission failed", description: msg });
    } finally {
      setLoading(false);
    }
  };

  const featured = reviews[0];

  return (
    <section id="contact" className="border-b border-white/[0.06] relative">
      <div className="absolute inset-0 radial-fade opacity-70" />
      <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-[0.9fr_1.1fr] gap-10">
        {/* Left info card */}
        <div className="space-y-6">
          <div>
            <div className="section-label mb-4">Get In Touch</div>
            <h2 className="section-heading text-[28px] sm:text-[34px] md:text-[44px] leading-[1.05]">
              Request a Custom Quote
            </h2>
            <p className="mt-5 text-[14.5px] leading-relaxed text-[#b6b3ab]">
              Ready to kick off your project? Drop us a message with your
              yardage dimensions or desired aggregate. We usually respond with
              full hauling dispatch slots in under 2 hours.
            </p>
          </div>

          <div className="card-outline rounded-xl p-5 space-y-4">
            <InfoRow
              icon={Phone}
              label="Dispatch Number"
              value={companyInfo.phone}
              href={`tel:${companyInfo.phone}`}
            />
            <div className="border-t divider-soft" />
            <InfoRow
              icon={Mail}
              label="Office Email"
              value={companyInfo.email}
              href={`mailto:${companyInfo.email}`}
            />
            <div className="border-t divider-soft" />
            <InfoRow
              icon={MapPin}
              label="Service Territory"
              value={companyInfo.location}
            />
          </div>

          <div className="card-outline rounded-xl p-5">
            <div className="flex items-center gap-0.5 mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={13}
                  className="fill-[#c9b98a] text-[#c9b98a]"
                />
              ))}
            </div>
            <p className="text-[13.5px] text-[#c8c5bc] leading-relaxed">
              &ldquo;{featured.text}&rdquo;
            </p>
            <div className="mt-4 text-[12.5px] text-[#8a877f]">
              <span className="text-[#f4efe6] font-semibold">
                {featured.name}
              </span>{" "}
              — {featured.location} • {featured.project}
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="card-outline rounded-xl p-6 md:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField
              label="Your Name / Company"
              required
              value={form.name}
              onChange={(v) => update("name", v)}
              placeholder="John Smith / Smith Contracting"
            />
            <FormField
              label="Phone Number"
              required
              value={form.phone}
              onChange={(v) => update("phone", v)}
              placeholder="(208) 000-0000"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField
              label="Email Address"
              type="email"
              value={form.email}
              onChange={(v) => update("email", v)}
              placeholder="you@example.com"
            />
            <FormField
              label="Project City / Location"
              value={form.city}
              onChange={(v) => update("city", v)}
              placeholder="Twin Falls, ID"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField
              label="Service Type"
              value={form.serviceType}
              onChange={(v) => update("serviceType", v)}
              options={serviceTypes}
            />
            <SelectField
              label="Material Class"
              value={form.materialClass}
              onChange={(v) => update("materialClass", v)}
              options={materialClasses}
            />
          </div>
          <FormField
            label="Yards Needed (Est)"
            value={form.yards}
            onChange={(v) => update("yards", v)}
            placeholder="e.g. 10 yards"
          />
          <div>
            <label className="block text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#a9a69d] mb-2">
              Description / Dimensions / Notes
            </label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Tell us about your project timeline, drop-off site, gate access..."
              className="field w-full rounded-md px-4 py-3 text-[13.5px] resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-md text-[13.5px] font-semibold disabled:opacity-70"
          >
            <Send size={15} />
            {loading ? "Sending..." : "Submit Real-Time Quote Request"}
          </button>
          <div className="flex items-center justify-center gap-2 text-[11.5px] text-[#8a877f]">
            <Lock size={12} />
            data is secured locally. No spam guaranteed.
          </div>
        </form>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap
      href={href}
      className="flex items-center gap-3 group"
    >
      <div className="h-10 w-10 rounded-lg chip flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[#c9b98a]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8a877f] font-semibold">
          {label}
        </div>
        <div className="text-[13.5px] text-[#f4efe6] font-medium truncate group-hover:text-white">
          {value}
        </div>
      </div>
    </Wrap>
  );
}

function FormField({ label, required, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#a9a69d] mb-2">
        {label} {required && <span className="text-[#c9b98a]">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field w-full rounded-md px-4 py-3 text-[13.5px]"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#a9a69d] mb-2">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="field w-full rounded-md px-4 py-3 text-[13.5px] appearance-none pr-10"
        >
          {options.map((o) => (
            <option key={o} value={o} className="bg-[#0f0f11] text-[#f4efe6]">
              {o}
            </option>
          ))}
        </select>
        <ChevronDown
          size={15}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a877f] pointer-events-none"
        />
      </div>
    </div>
  );
}
